import { Injectable } from '@angular/core';

import { NotificationsConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { BettingOfferVersion, DisabledNotificationSport } from '@frontend/sports/types/components/features';

import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../../core/picks/betslip-v2-pick';

export interface INotificationStatus {
    isAppNotificationEnabled: boolean;
    isEmailNotificationEnabled: boolean;
}

@Injectable()
export default class NotificationService {
    constructor(private notificationConfig: NotificationsConfig) {}

    /**
     * Get global notification status
     * If status is disabled then we don't provide it for selection from user
     */
    globalNotificationStatus(): INotificationStatus {
        return {
            isAppNotificationEnabled: this.notificationConfig.isAppBetNotificationsEnabled,
            isEmailNotificationEnabled: this.notificationConfig.isEmailBetNotificationsEnabled,
        };
    }

    /**
     * Get notification status from app or email notification depending of pick selection
     * Email notifications are disabled for bet builder picks.
     * Checks if the notification type is generally disabled or the sport is disabled for this type.
     *
     * @param picks
     */
    notificationStatus(picks: BetslipPick[], notificationStatus?: INotificationStatus): INotificationStatus {
        const sports = NotificationService.collectSports(picks);
        const hasBetBuilderPicks = picks.some((p) => BetslipBetBuilderPick.isPick(p));
        const globalNotificationStatus = notificationStatus ?? this.globalNotificationStatus();

        return {
            isAppNotificationEnabled:
                globalNotificationStatus.isAppNotificationEnabled &&
                !this.isSportDisabled(sports, this.notificationConfig.appBetNotificationsDisabledForSports),
            isEmailNotificationEnabled:
                globalNotificationStatus.isEmailNotificationEnabled &&
                !hasBetBuilderPicks &&
                !this.isSportDisabled(sports, this.notificationConfig.emailDisabledForSports),
        };
    }

    private isSportDisabled(sports: [SportConstant[], SportConstant[], SportConstant[]], disabledSports?: DisabledNotificationSport[]): boolean {
        if (!disabledSports?.length) {
            return false;
        }
        const [v1, v2, all] = sports;

        return disabledSports.some(
            (ds) =>
                (ds.version === BettingOfferVersion.V1 && v1.some((s) => s === ds.sportId)) ||
                (ds.version === BettingOfferVersion.V2 && v2.some((s) => s === ds.sportId)) ||
                (!ds.version && all.some((s) => s === ds.sportId)),
        );
    }

    private static collectSports(picks: BetslipPick[]): [SportConstant[], SportConstant[], SportConstant[]] {
        const v1Sports: SportConstant[] = [];
        const v2Sports: SportConstant[] = [];
        const all: SportConstant[] = [];
        for (const pick of picks) {
            if (BetslipV1Pick.isPick(pick)) {
                v1Sports.push(pick.sportId);
            }
            if (BetslipV2Pick.isPick(pick)) {
                v2Sports.push(pick.sportId);
            }
            all.push(pick.sportId);
        }

        return [v1Sports, v2Sports, all];
    }
}
